import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { Button, Col, Input, Label, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { ConceptNode, NetworkConceptsFilter, loadAxesForConcept, selectAxesForConcept, clearAxesForConcepts } from '../../redux/AccountsSlice';
import { bulkReassignColumnDimension, DimensionReference, DimensionReferenceInput, createDimensionReferenceFromInput } from '../../redux/AfrSlice';
import { Concept } from '../../redux/ConceptSlice';
import { Node, selectUnitHierarchy } from '../../redux/EntitiesSlice';
import { isAdmin } from '../../redux/MiscSlice';
import {
    AccountScope, addDimensionalScopeChange, addEntityScopeChange,
    ConceptInput, DimensionalConceptRefInput, getTaxonomyChanges, RelationshipInput,
    TaxonomyChangesState
} from '../../redux/TaxonomyChangesSlice';
import {
    AccountRootDependency, AccountDependency, DtsConcept, getSelectedTaxonomyConcept,
    resetTaxonomyConceptState
} from '../../redux/TaxonomyConceptSlice';
import { clearSelectedReference, loadAllTaxonomyReferences } from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, getReferenceForVersion, selectCurrentVersion } from '../../redux/TaxonomyVersionSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { SelectableListItem } from './BypassRequest';

export interface RulesEditorProps {
    currentConcept: DtsConcept | null;
    accountTree: Array<ConceptNode>;
    handleUnitScopeChange: (currentNode: any, selectedNodes: any) => boolean;
    conceptChanges: ConceptInput[];
    relationshipChanges: RelationshipInput[];
    versionInfo: any;
    handleDependencyAccountList: (accountTree: ConceptNode[] | null, selections: string[],
        versionInfo: any, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[]) => Array<SelectableListItem>;
}
interface DependencyTableData {
    id: string;
    dependency: EditableAccountDependency | EditableAccountSubDependency;
    accounts: string;
    relationship: string;
    value: string;
    name: string;
    _children: DependencyTableData[] | null;
}
// NOTE - This is a variation of the interface defined in ConceptSlice. Here we add a "code" attribute to use in the table for conciseness
export interface ConceptDisplayRef {
    conceptName: string;
    conceptId: string;
    code: string;
    afrYear: number;
}
interface EditableAccountSubDependency {
    editable: boolean,
    year: number | null;
    name: string | null;
    target: ConceptDisplayRef | null;
    dependencies: EditableAccountSubDependency[];
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    changed: boolean;
    remove: boolean;
    dimensionQualifiers: DimensionReference[] | null;
}
interface EditableAccountDependency {
    ruleId: string | null;
    editable: boolean,
    year: number | null;
    id: string;
    name: string | null;
    target: ConceptDisplayRef | null;
    dependencies: EditableAccountSubDependency[] | null;
    preconditions: EditableAccountSubDependency[] | null;
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    message: string | null;
    changed: boolean;
    remove: boolean;
    dimensionQualifiers: DimensionReference[] | null;
    dimensionsToMatch: DimensionReference[] | null;
}

export interface StyledSelectableListItem {
    label: string;
    value: string;
    disabled: boolean;
    checked: boolean;
    className: string;
    children: Array<StyledSelectableListItem>;
}

const EligibilityRulesEditor = (props: RulesEditorProps ) => {
    const dispatch = useAppDispatch();
    const isAdminUser: boolean = useAppSelector(isAdmin);
    var versionedConcept: DtsConcept | null = useAppSelector(getSelectedTaxonomyConcept);
    var changes: TaxonomyChangesState | null = useAppSelector(getTaxonomyChanges);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    var unitHierarchy: Node | null = useAppSelector(selectUnitHierarchy);
    var concept: Concept | null = versionedConcept ? versionedConcept.concept : null;
    var axesForConcept: ConceptNode[] = useAppSelector(selectAxesForConcept);

    const [versionYear, setVersionYear] = useState(versionInfo ? versionInfo.year : 2022);
    const [scopeChanges, setScopeChanges] = useState<string[]>([]);
    const [accountId, setAccountId] = useState<string>((versionedConcept && versionedConcept.concept) ? versionedConcept.concept.id : "")
    var dependencyTabulatorRef = React.useRef<any>();
    const [depAccounts, setDepAccounts] = useState<string>("");
    const [depRelationship, setDepRelationship] = useState<string>("");
    const [depValue, setDepValue] = useState<string>("");
    const [depBooleanValue, setDepBooleanValue] = useState<string>("");
    const [depTest, setDepTest] = useState<string>("");
    const [depName, setDepName] = useState<string>("");
    const [depMessage, setDepMessage] = useState<string>("");
    const [depFallback, setDepFallback] = useState<string>("");
    const [depBooleanFallback, setDepBooleanFallback] = useState<string>("");
    const [depIsSequence, setDepIsSequence] = useState<string>("");
    const [selectedDependency, setSelectedDependency] = useState<EditableAccountDependency | EditableAccountSubDependency | null>(null);
    const [selectedDependencyIsRoot, setSelectedDependencyIsRoot] = useState<boolean>(true);
    const [canMakeRootDependencyEditable, setCanMakeRootDependencyEditable] = useState<boolean>(false);

    const findConceptNodeRecursive = function (cn: ConceptNode, accountId: string): ConceptNode | null {
        if (cn.concept && cn.concept.id === accountId) {
            return cn;
        }
        if (!cn.children) {
            return null;
        }
        var cnResult : ConceptNode | null;
        for (var cnChild of cn.children) {
            cnResult = findConceptNodeRecursive(cnChild, accountId);
            if (cnResult) {
                return cnResult;
            }
        }
        return null;
    }
    const findConceptNode = function (accountId: string): ConceptNode | null {
        var foundNode: ConceptNode | null = null;
        if (!props.accountTree) {
            return null;
        }
        for (var root of props.accountTree) {
            foundNode = findConceptNodeRecursive(root, accountId);
            if (foundNode) {
                return foundNode;
            }
        }
        return foundNode;
    }
    const createConceptDisplayRefFromId = function (accountId: string, year : number ): ConceptDisplayRef {
        var cn: ConceptNode | null = findConceptNode(accountId);
        if (!cn) {
            var notFound: ConceptDisplayRef = {
                conceptId: accountId,
                conceptName: accountId,
                code: "no code",
                afrYear: year
            };
            return notFound;
        }
        var result: ConceptDisplayRef = {
            conceptId: accountId,
            conceptName: cn.concept.name,
            code: cn.concept.code,
            afrYear: year
        }
        return result;
    }
    const createDimensionReferenceList = function (drInputList: DimensionReferenceInput[] | null): DimensionReference[] {
        var results: DimensionReference[] = [];
        if (drInputList) {
            drInputList.map(dri => {
                results.push(createDimensionReferenceFromInput(dri));
            });
        }
        return results;
    }
    const getNodeIcon = function (concept: Concept | null): string {
        if (!concept) {
            return "";
        }
        if (concept.code < "2") {
            return ""; // county
        }
        if (concept.code < "3") {
            return ""; // city
        }
        if (concept.code < "5") {
            return ""; // special district
        }
        if (concept.code < "9") {
            return ""; // other entity
        }
        return ""; // organizing node
    }
    const addNode = function (parent: StyledSelectableListItem | null, n: Node, selIdMap : any): StyledSelectableListItem | null {
        var nodeLabel = n.concept ? n.concept.name : n.name;
        var nvalue = n.concept ? n.concept.id : n.id;
        const isSelected: boolean = selIdMap.hasOwnProperty(nvalue);
        var listItem: StyledSelectableListItem = {
            label: nodeLabel,
            value: nvalue,
            disabled: false,
            checked: isSelected,
            className: getNodeIcon(concept),
            children: []
        }
        if (parent) {
            parent.children.push(listItem);
        }
        if (n.outgoing) {
            n.outgoing.map(e => addNode(listItem, e.to,selIdMap));
        }
        return listItem;
    }
    const addConceptNode = function (parent: StyledSelectableListItem | null, n: ConceptNode, selIdMap: any): StyledSelectableListItem | null {
        var nodeLabel = n.concept ? n.concept.name : "na";
        var axisId: string = "";
        if (parent) {
            var parts: string[] = parent.value.split("#");
            axisId = parts[0] + "#";
        }
        var cid = n.concept ? n.concept.id : "na";
        var nvalue = axisId + cid;
        const isSelected: boolean = selIdMap.hasOwnProperty(cid);
        var listItem: StyledSelectableListItem = {
            label: nodeLabel,
            value: nvalue,
            disabled: false,
            checked: isSelected,
            className: getNodeIcon(concept),
            children: []
        }
        if (parent) {
            parent.children.push(listItem);
        }
        if (n.children) {
            n.children.map(e => addConceptNode(listItem, e, selIdMap));
        }
        return listItem;
    }
    const addRootNode = function (alist: Array<StyledSelectableListItem>, root: StyledSelectableListItem | null) {
        if (root) {
            alist.push(root);
        }
    }
    const getUnitsList = function (unitHierarchy : Node | null,selectedConcept : DtsConcept | null): StyledSelectableListItem[] {
        var alist: Array<StyledSelectableListItem> = [];
        if (unitHierarchy && unitHierarchy.outgoing) {
            var selectedIdMap : any = {};
            if (selectedConcept && selectedConcept.concept ) {
                // Check changes first 
                var selId = selectedConcept.concept.id;
                if (changes && changes.entityScopeChanges && changes.entityScopeChanges.hasOwnProperty(selId)) {
                    const mapping: string[] = changes.entityScopeChanges[selId];
                    mapping.map(scid => selectedIdMap[scid] = selectedConcept.concept);
                } else if (selectedConcept.entityScope) {
                    selectedConcept.entityScope.map(es => selectedIdMap[es.id] = es);
                }
            }
            unitHierarchy.outgoing.map(e => addRootNode(alist, addNode(null, e.to, selectedIdMap)));
        }
        return alist;
    }
    const [unitsList, setUnitsList] = useState<StyledSelectableListItem[]>([]);

    const getDimensionsList = function (selectedConcept: DtsConcept | null): StyledSelectableListItem[] {
        var alist: Array<StyledSelectableListItem> = [];
        if (selectedConcept && selectedConcept.dimensionalBreakdowns) {
            var selectedIdMap: any = {};
            if (selectedConcept && selectedConcept.concept) {
                // Check changes first 
                var selId = selectedConcept.concept.id;
                if (changes && changes.dimensionalScopeChanges && changes.dimensionalScopeChanges.hasOwnProperty(selId)) {
                    const mapping: string[] = changes.dimensionalScopeChanges[selId];
                    mapping.map(scid => selectedIdMap[scid] = selectedConcept.concept);
                } else if (selectedConcept.dimensionalScope) {
                    selectedConcept.dimensionalScope.map(es => selectedIdMap[es.member != null ? es.member.id : ""] = es);
                }
            }
            selectedConcept.dimensionalBreakdowns.map(e => addRootNode(alist, addConceptNode(null, e, selectedIdMap)));
        }
        return alist;
    }
    const [dimensionsList, setDimensionsList] = useState<StyledSelectableListItem[]>([]);


    const addDimensionChildren = function (assignedMap : any, axisId: string, parentNode: ConceptNode, items: SelectableListItem[]) {
        if (!parentNode.children || parentNode.children.length == 0) {
            return;
        }
        parentNode.children.map(cn => {
            var childItems: SelectableListItem[] = [];
            addDimensionChildren(assignedMap, axisId, cn, childItems);
            var key: string = axisId + '#' + cn.concept.id;
            var item: SelectableListItem = {
                label: cn.concept.name,
                value: key,
                disabled: false,
                checked: assignedMap.hasOwnProperty(key),
                children: childItems
            }
            items.push(item);
        }
        );

    }
    const ddhandleUnitChange = function (currentNode: any, selectedNodes: any) {
        if (!versionedConcept || !versionedConcept.concept) {
            return;
        }
        var sids: string[] = [];
        selectedNodes.map((sn : StyledSelectableListItem) => sids.push(sn.value));
        const scopeChange: AccountScope = {
            accountId: versionedConcept.concept.id,
            scopeIds: sids
        }
        dispatch(addEntityScopeChange(scopeChange));
    }

    const ddhandleDimensionChange = function (currentNode: any, selectedNodes: any) {
        if (!versionedConcept || !versionedConcept.concept) {
            return;
        }
        var sids: string[] = [];
        selectedNodes.map((sn: StyledSelectableListItem) => sids.push(sn.value));
        const scopeChange: AccountScope = {
            accountId: versionedConcept.concept.id,
            scopeIds: sids
        }
        dispatch(addDimensionalScopeChange(scopeChange));
    }

    useEffect(() => {
        dispatch(clearSelectedReference());
        setUnitsList(getUnitsList(unitHierarchy,versionedConcept));
        setDimensionsList(getDimensionsList(versionedConcept));
    }, [versionedConcept]);

    useEffect(() => {
        var crefList = changes ? changes.referenceChanges : [];
        //        console.log("use effect", changes ? changes.referenceChanges : null );
//        setUnitsList(getUnitsList(unitHierarchy,versionedConcept));
    }, [changes]);

    useEffect(() => {
        var crefList = changes ? changes.referenceChanges : [];
        //        console.log("use effect", changes ? changes.referenceChanges : null );
        setUnitsList(getUnitsList(unitHierarchy,versionedConcept));
    }, [unitHierarchy]);


    const secondElementStyle = {
        marginTop: '4pt',
        marginLeft: '1em'
    }
    const unitScopeSelector = useMemo(() => {
        return (
        <DropdownTreeSelect
            id="unitPicker"
            data={unitsList}
            keepTreeOnSearch={true} keepChildrenOnSearch={true}
            showPartiallySelected={true}
            onChange={ddhandleUnitChange}
            texts={{ placeholder: "Choose Unit..." }} />
        )
    }, [unitHierarchy, versionedConcept,props]);

    const breakdownScopeSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dimensionPicker"
                data={dimensionsList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleDimensionChange}
                texts={{ placeholder: "Choose Dimension..." }} />
        )
    }, [unitHierarchy, versionedConcept, props]);


    return (
        <React.Fragment>
            <Row>
                <Col>
                    <Row>
                        <Col md="2" className="text-md-right" >
                            <Label><b>Eligiblity Restrictions:</b></Label>
                        </Col>
                        <Col md="9">
                            {versionedConcept && versionedConcept.dimensionalBreakdowns
                                ?
                                <Row>
                                    <Col>
                                        <Label style={secondElementStyle}><b>Entity:</b></Label>
                                        <h5 style={secondElementStyle}>
                                            {unitScopeSelector}
                                        </h5>
                                    </Col>
                                    <Col>
                                        <Label style={secondElementStyle}><b>Breakdown:</b></Label>
                                        <h5 style={secondElementStyle}>
                                            {breakdownScopeSelector}
                                        </h5>
                                    </Col>
                                </Row>
                                    :
                                <Row>
                                    <Col>
                                        <Label style={secondElementStyle}><b>Entity:</b></Label>
                                        <h5 style={secondElementStyle}>
                                            {unitScopeSelector}
                                        </h5>
                                    </Col>
                                </Row>
                         }
                        </Col>
                    </Row>
                </Col>
            </Row>
        </React.Fragment>
    );
}
export default EligibilityRulesEditor;